import type { Query, QueryKey, UseQueryOptions } from '@tanstack/react-query';
import type { z } from 'zod';
import {
  monitoringIntegrationDeleteResultSchema,
  monitoringIntegrationCreateSchema,
  monitoringIntegrationUpdateSchema,
  monitoringIntegrationVersionCommandSchema,
  monitoringIntegrationRetireSchema,
  monitoringIntegrationPathSchema,
  monitoringIntegrationListQuerySchema,
  monitoringIntegrationCommandResultSchema,
  monitoringIntegrationDetailSchema,
  monitoringIntegrationPageSchema,
  monitoringProviderConfigurationSchema,
} from '@solidops/shared/webhooks';
import type {
  MonitoringIntegrationDeleteResultDto,
  MonitoringIntegrationCreateDto,
  MonitoringIntegrationUpdateDto,
  MonitoringIntegrationCommandResultDto,
  MonitoringIntegrationDetailDto,
  MonitoringIntegrationPageDto,
  MonitoringProviderConfigurationDto,
} from '@solidops/shared/webhooks';
import { incidentIdempotencyKeySchema } from '@solidops/shared/incidents';

import { ApiError, queryClient } from './client.js';
import {
  deleteMonitoringIntegration as deleteRequest,
  createMonitoringIntegration as createRequest,
  updateMonitoringIntegration as updateRequest,
  enableMonitoringIntegration as enableRequest,
  disableMonitoringIntegration as disableRequest,
  rotateMonitoringCredential as rotateRequest,
  retireMonitoringIntegration as retireRequest,
  getMonitoringIntegration as getRequest,
  listMonitoringIntegrations as listRequest,
  getMonitoringProviderConfiguration as getProviderRequest,
  getGetMonitoringIntegrationQueryKey,
  getGetMonitoringIntegrationQueryOptions,
  getListMonitoringIntegrationsQueryKey,
  getListMonitoringIntegrationsQueryOptions,
  getGetMonitoringProviderConfigurationQueryKey,
  getGetMonitoringProviderConfigurationQueryOptions,
} from './generated/webhooks/webhooks.js';
import type { ListMonitoringIntegrationsParams } from './generated/model/index.js';

export type WebhookAuthority = Readonly<{ sessionId: string; userId: string; role: 'Admin' }>;
type ReadOptions = { signal?: AbortSignal };
type WebhookCommandAuth = { csrfToken: string; idempotencyKey: string };
type WebhookQueryOptions<T> = UseQueryOptions<T, ApiError> & { queryKey: QueryKey };

export function webhookAuthorityKey(authority: WebhookAuthority | null): string {
  return authority
    ? `webhook-authority:${authority.sessionId}:${authority.userId}:${authority.role}`
    : 'webhook-authority:none';
}
function scopedKey(key: QueryKey, authority: WebhookAuthority | null): QueryKey {
  return [...key, webhookAuthorityKey(authority)];
}
function isWebhookQuery(query: { queryKey: QueryKey }): boolean {
  const path = query.queryKey[0];
  const root = getListMonitoringIntegrationsQueryKey()[0];
  return typeof path === 'string' && (path === root || path.startsWith(`${root}/`));
}
function belongsTo(query: { queryKey: QueryKey }, authority: WebhookAuthority | null): boolean {
  return isWebhookQuery(query) && query.queryKey.at(-1) === webhookAuthorityKey(authority);
}
export function webhookReadAccessLost(
  key: QueryKey,
  error: unknown,
  authority: WebhookAuthority | null,
  pathname: string,
): boolean {
  if (!authority || !belongsTo({ queryKey: key }, authority) || !(error instanceof ApiError))
    return false;
  if (![401, 403, 404].includes(error.status)) return false;
  const root = getListMonitoringIntegrationsQueryKey()[0];
  const route = /^\/(?:settings|teams\/([^/]+))\/webhooks(?:\/([^/]+))?$/.exec(pathname);
  if (!route) return false;
  const integrationId = route[2];
  if (!integrationId) {
    const params = monitoringIntegrationListQuerySchema.safeParse(key[1]);
    return key[0] === root && params.success && (params.data.teamId ?? null) === (route[1] ?? null);
  }
  const currentPath = `${root}/${integrationId}`;
  return key[0] === currentPath || key[0] === `${currentPath}/provider-configuration`;
}
function evictWebhookRead(key: QueryKey, previous: Query[]): void {
  const current = queryClient.getQueryCache().findAll({ queryKey: key, exact: true });
  for (const query of new Set([...previous, ...current])) {
    query.setState({ data: undefined, dataUpdatedAt: 0 });
    if (query.state.fetchStatus !== 'fetching')
      queryClient.removeQueries({ queryKey: query.queryKey, exact: true });
  }
}

async function readWebhook<T>(
  request: () => Promise<unknown>,
  schema: z.ZodType<T>,
  key: QueryKey,
  signal: AbortSignal,
): Promise<T> {
  const previous = queryClient.getQueryCache().findAll({ queryKey: key, exact: true });
  try {
    return schema.parse(await request());
  } catch (error) {
    if (!signal.aborted && error instanceof ApiError && [401, 403, 404].includes(error.status))
      evictWebhookRead(key, previous);
    throw error;
  }
}

export function getWebhookQueryKey(id: string, authority: WebhookAuthority | null): QueryKey {
  return scopedKey(getGetMonitoringIntegrationQueryKey(id), authority);
}
export function getWebhookQueryOptions(
  id: string,
  authority: WebhookAuthority | null,
): WebhookQueryOptions<MonitoringIntegrationDetailDto> {
  const key = getWebhookQueryKey(id, authority);
  return {
    ...getGetMonitoringIntegrationQueryOptions<MonitoringIntegrationDetailDto, ApiError>(id),
    queryKey: key,
    queryFn: async ({ signal }) =>
      readWebhook(
        () =>
          getRequest(monitoringIntegrationPathSchema.parse({ integrationId: id }).integrationId, {
            signal,
          }),
        monitoringIntegrationDetailSchema,
        key,
        signal,
      ),
  };
}
export function getWebhooksQueryOptions(
  input: ListMonitoringIntegrationsParams = {},
  authority: WebhookAuthority | null,
): WebhookQueryOptions<MonitoringIntegrationPageDto> {
  const params = monitoringIntegrationListQuerySchema.parse(input);
  const key = scopedKey(getListMonitoringIntegrationsQueryKey(params), authority);
  return {
    ...getListMonitoringIntegrationsQueryOptions<MonitoringIntegrationPageDto, ApiError>(params),
    queryKey: key,
    queryFn: async ({ signal }) =>
      readWebhook(
        () => listRequest(params, { signal }),
        monitoringIntegrationPageSchema,
        key,
        signal,
      ),
  };
}
export function getWebhookProviderQueryOptions(
  id: string,
  authority: WebhookAuthority | null,
): WebhookQueryOptions<MonitoringProviderConfigurationDto> {
  const key = scopedKey(getGetMonitoringProviderConfigurationQueryKey(id), authority);
  return {
    ...getGetMonitoringProviderConfigurationQueryOptions<
      MonitoringProviderConfigurationDto,
      ApiError
    >(id),
    queryKey: key,
    queryFn: async ({ signal }) =>
      readWebhook(
        () =>
          getProviderRequest(
            monitoringIntegrationPathSchema.parse({ integrationId: id }).integrationId,
            { signal },
          ),
        monitoringProviderConfigurationSchema,
        key,
        signal,
      ),
  };
}

/** Raw commands bypass the mutation cache; only the current workflow may handle their transient result. */
export async function createWebhook(
  input: MonitoringIntegrationCreateDto,
  auth: WebhookCommandAuth,
  options?: ReadOptions,
): Promise<MonitoringIntegrationCommandResultDto> {
  return monitoringIntegrationCommandResultSchema.parse(
    await createRequest(monitoringIntegrationCreateSchema.parse(input), {
      ...options,
      headers: {
        'x-csrf-token': auth.csrfToken,
        'Idempotency-Key': incidentIdempotencyKeySchema.parse(auth.idempotencyKey),
      },
    }),
  );
}
export async function updateWebhook(
  id: string,
  input: MonitoringIntegrationUpdateDto,
  auth: WebhookCommandAuth,
  options?: ReadOptions,
): Promise<MonitoringIntegrationCommandResultDto> {
  return monitoringIntegrationCommandResultSchema.parse(
    await updateRequest(
      monitoringIntegrationPathSchema.parse({ integrationId: id }).integrationId,
      monitoringIntegrationUpdateSchema.parse(input),
      {
        ...options,
        headers: {
          'x-csrf-token': auth.csrfToken,
          'Idempotency-Key': incidentIdempotencyKeySchema.parse(auth.idempotencyKey),
        },
      },
    ),
  );
}
function maintenanceOptions(auth: WebhookCommandAuth, options?: ReadOptions): RequestInit {
  return {
    ...options,
    headers: {
      'x-csrf-token': auth.csrfToken,
      'Idempotency-Key': incidentIdempotencyKeySchema.parse(auth.idempotencyKey),
    },
  };
}
export async function enableWebhook(
  id: string,
  expectedVersion: number,
  auth: WebhookCommandAuth,
  options?: ReadOptions,
): Promise<MonitoringIntegrationCommandResultDto> {
  return monitoringIntegrationCommandResultSchema.parse(
    await enableRequest(
      monitoringIntegrationPathSchema.parse({ integrationId: id }).integrationId,
      monitoringIntegrationVersionCommandSchema.parse({ expectedVersion }),
      maintenanceOptions(auth, options),
    ),
  );
}
export async function disableWebhook(
  id: string,
  expectedVersion: number,
  auth: WebhookCommandAuth,
  options?: ReadOptions,
): Promise<MonitoringIntegrationCommandResultDto> {
  return monitoringIntegrationCommandResultSchema.parse(
    await disableRequest(
      monitoringIntegrationPathSchema.parse({ integrationId: id }).integrationId,
      monitoringIntegrationVersionCommandSchema.parse({ expectedVersion }),
      maintenanceOptions(auth, options),
    ),
  );
}
export async function rotateWebhookCredential(
  id: string,
  expectedVersion: number,
  auth: WebhookCommandAuth,
  options?: ReadOptions,
): Promise<MonitoringIntegrationCommandResultDto> {
  return monitoringIntegrationCommandResultSchema.parse(
    await rotateRequest(
      monitoringIntegrationPathSchema.parse({ integrationId: id }).integrationId,
      monitoringIntegrationVersionCommandSchema.parse({ expectedVersion }),
      maintenanceOptions(auth, options),
    ),
  );
}
export async function retireWebhook(
  id: string,
  expectedVersion: number,
  auth: WebhookCommandAuth,
  options?: ReadOptions,
): Promise<MonitoringIntegrationCommandResultDto> {
  return monitoringIntegrationCommandResultSchema.parse(
    await retireRequest(
      monitoringIntegrationPathSchema.parse({ integrationId: id }).integrationId,
      monitoringIntegrationRetireSchema.parse({ expectedVersion, confirmed: true }),
      maintenanceOptions(auth, options),
    ),
  );
}
export async function deleteWebhook(
  id: string,
  body: { readonly expectedVersion: number },
  auth: WebhookCommandAuth,
  options?: ReadOptions,
): Promise<MonitoringIntegrationDeleteResultDto> {
  return monitoringIntegrationDeleteResultSchema.parse(
    await deleteRequest(
      monitoringIntegrationPathSchema.parse({ integrationId: id }).integrationId,
      monitoringIntegrationVersionCommandSchema.parse(body),
      maintenanceOptions(auth, options),
    ),
  );
}
export async function invalidateWebhookQueries(authority: WebhookAuthority): Promise<void> {
  await queryClient.invalidateQueries({ predicate: (query) => belongsTo(query, authority) });
}
export function evictWebhookQueries(authority: WebhookAuthority | null): void {
  for (const query of queryClient
    .getQueryCache()
    .findAll({ predicate: (query) => belongsTo(query, authority) })) {
    query.setState({ data: undefined, dataUpdatedAt: 0 });
    queryClient.removeQueries({ queryKey: query.queryKey, exact: true });
  }
}
